import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { Company } from "@/lib/types";

export interface CompanyFormValues {
  name: string;
  razon_social: string;
  email: string;
  phone: string;
  rubro: string;
  is_active: boolean;
  notes: string;
}

interface Props {
  initial?: Partial<Company>;
  value: CompanyFormValues;
  onChange: (next: CompanyFormValues) => void;
}

export function emptyCompanyForm(): CompanyFormValues {
  return {
    name: "",
    razon_social: "",
    email: "",
    phone: "",
    rubro: "",
    is_active: true,
    notes: "",
  };
}

export function fromCompany(c: Partial<Company>): CompanyFormValues {
  return {
    name: c.name ?? "",
    razon_social: c.razon_social ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    rubro: c.rubro ?? "",
    is_active: c.is_active ?? true,
    notes: c.notes ?? "",
  };
}

export function CompanyForm({ value, onChange }: Props) {
  const upd = <K extends keyof CompanyFormValues>(
    k: K,
    v: CompanyFormValues[K]
  ) => onChange({ ...value, [k]: v });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="name">Nombre</Label>
          <Input
            id="name"
            placeholder="Anfibia S.A."
            value={value.name}
            onChange={(e) => upd("name", e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="razon">Razón Social</Label>
          <Input
            id="razon"
            placeholder="Anfibia Tecnología S.A."
            value={value.razon_social}
            onChange={(e) => upd("razon_social", e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email de contacto</Label>
          <Input
            id="email"
            type="email"
            placeholder="contacto@empresa.com"
            value={value.email}
            onChange={(e) => upd("email", e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phone">Teléfono</Label>
          <Input
            id="phone"
            placeholder="+54 11 1234-5678"
            value={value.phone}
            onChange={(e) => upd("phone", e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="rubro">Rubro</Label>
        <Input
          id="rubro"
          placeholder="Fintech, SaaS, Salud, Retail, etc."
          value={value.rubro}
          onChange={(e) => upd("rubro", e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="notes">Notas internas</Label>
        <Textarea
          id="notes"
          placeholder="Comentarios sobre el cliente, preferencias, contactos, etc."
          value={value.notes}
          onChange={(e) => upd("notes", e.target.value)}
          rows={4}
        />
        <p className="text-[11px] text-muted-foreground">
          Solo visibles para vos. No se comparten con el cliente ni con candidatos.
        </p>
      </div>

      <label className="flex items-center gap-2.5 cursor-pointer pt-2 border-t border-border pt-4">
        <Checkbox
          checked={value.is_active}
          onCheckedChange={(v) => upd("is_active", !!v)}
        />
        <span className="text-sm">
          Cliente <strong>activo</strong>
          <span className="text-muted-foreground ml-2 text-xs">
            (los inactivos no aparecen en el dropdown del header)
          </span>
        </span>
      </label>
    </div>
  );
}

/** Hook helper para usar en pages — devuelve [value, setValue, reset]. */
export function useCompanyForm(initial?: Partial<Company>) {
  return useState<CompanyFormValues>(
    initial ? fromCompany(initial) : emptyCompanyForm()
  );
}
